import React, { useRef, useState } from 'react';
import { css } from '@emotion/react';
import { OnboardingAiProfileType, AiProfileInfo } from '@/customTypes/chapter';
import StoryGameAiImageCropPopup from './StoryGameAiImageCropPopup';
import { CloseIconStoryGame } from 'public/assets';
import theme from '@/styles/theme';
import { IS_IOS_SAFARI } from '@/lib/common-react/utils/environment';
import useTranslation from 'next-translate/useTranslation';

interface StoryGameAiImageUploadProps {
  setAiProfileType: (type: OnboardingAiProfileType) => void;
  onChangeAiProfile: (newAiProfileInfo: AiProfileInfo) => void;
  aiProfileInfo: AiProfileInfo | undefined;
}
const container = css`
  display: flex;
  flex-direction: column;
  width: 400px;
  padding: 1.5rem 1.25rem;
  background: rgba(0, 0, 0, 0.75);
  border-radius: 0.625rem;
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  backdrop-filter: blur(12px);
  @media (max-width: ${theme.devices.md}px) {
    width: 100%;
  }
`;

const headerStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const titleStyle = css`
  color: var(--whiteA90, rgba(255, 255, 255, 0.9));
  font-size: 18px;
  font-weight: 700;
  line-height: 26px; /* 144.444% */
`;

const closeButtonStyle = css`
  width: 1.5rem;
  height: 1.5rem;
  cursor: pointer;
`;

const uploadAreaStyle = css`
  border: 1px dashed var(--whiteA25, rgba(255, 255, 255, 0.25));
  background: var(--blackA25, rgba(0, 0, 0, 0.25));
  border-radius: 8px;
  padding: 36px 12px;
  text-align: center;
  margin-bottom: 20px;
`;

const iconStyle = css`
  margin-bottom: 12px;
`;

const buttonContainer = css`
  margin-top: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const uploadButtonStyle = css`
  background-color: #7e57c2;
  color: white;
  padding: 7px 12px;
  border-radius: 5px;
  cursor: pointer;
  justify-content: center;
  display: flex;
  align-self: center;

  color: var(--white, var(--Colors-Base-Neutral-White-white, #fff));

  font-size: 14px;
  gap: 4px;
  align-items: center;
`;

const noteStyle = css`
  border-radius: 8px;
  background: var(--whiteA05, rgba(255, 255, 255, 0.05));
  padding: 12px 16px;
`;
const text1 = css`
  color: var(--whiteA90, rgba(255, 255, 255, 0.9));
  text-align: center;
  font-size: var(--font-heading-xxs, 15px);
  font-weight: 500;
  line-height: 22px; /* 146.667% */
`;
const text2 = css`
  color: var(--whiteA75, rgba(255, 255, 255, 0.75));
  text-align: center;
  font-size: 13px;
  line-height: 18px; /* 138.462% */
`;

const text3 = css`
  color: var(--Colors-Base-Neutral-White-white, #fff);

  font-size: 13px;
  line-height: 18px; /* 138.462% */
`;

const text4 = css`
  color: var(--whiteA50, rgba(255, 255, 255, 0.5));

  font-size: 12px;
  line-height: 16px; /* 133.333% */
`;

const cropContainer = css`
  height: 100vh;
  justify-content: center;
  align-items: center;
  display: flex;
  ${IS_IOS_SAFARI ? 'max-height: -webkit-fill-available' : ''};
  // @supports (100dvh) {
  //   height: 100dvh;
  // }
  // @supports not (height: 100dvh) {
  //   height: 95vh;
  // }
  height: calc(var(--inner_vh, 1vh) * 100);
`;

const EmptyProfileIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="30"
    height="30"
    viewBox="0 0 30 30"
    fill="none"
  >
    <g clipPath="url(#clip0_1390_7008)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.3596 20.1352C10.6279 20.1352 6.79202 16.2993 6.79202 11.5676C6.79202 6.83584 10.6279 3 15.3596 3C20.0913 3 23.9272 6.83584 23.9272 11.5676C23.9272 16.2993 20.0913 20.1352 15.3596 20.1352ZM15.3307 49.5326C7.75948 49.5326 -5.5957 40.7257 0.587141 28.4014C5.07057 19.4646 7.01443 20.4563 10.0302 21.9948C11.4399 22.714 13.0838 23.5526 15.3307 23.5526C17.6581 23.5526 19.3326 22.6584 20.7327 21.9107C23.5759 20.3923 25.2878 19.4781 29.0396 27.8841C35.3874 42.1065 22.902 49.5326 15.3307 49.5326Z"
        fill="white"
        fillOpacity="0.25"
      />
    </g>
    <defs>
      <clipPath id="clip0_1390_7008">
        <rect width="30" height="30" rx="3" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const StoryGameAiImageUpload: React.FC<StoryGameAiImageUploadProps> = ({
  setAiProfileType,
  onChangeAiProfile,
  aiProfileInfo,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const onClose = () => {
    setAiProfileType(undefined);
  };
  if (selectedFile) {
    return (
      <div css={cropContainer}>
        <StoryGameAiImageCropPopup
          initialImage={selectedFile}
          onCrop={(croppedImage: File) => {
            onChangeAiProfile({ cropImageFile: croppedImage });
            setAiProfileType('AiImageFilter');
          }}
          onBack={() => {
            // 업로드된 파일이 있으면 setSelectedFile(null) + setAiProfileType('AiImageFilter')로 돌아가기
            // 아니면 setSelectedFile(null)
            setSelectedFile(null);
          }}
        />
      </div>
    );
  }

  return (
    <div css={container}>
      <div css={headerStyle}>
        <span css={titleStyle}>
          {t('storygame_ai_onboarding_popup_title_make_ai_game_profile')}
        </span>
        <button css={closeButtonStyle} onClick={onClose}>
          <CloseIconStoryGame />
        </button>
      </div>
      <div css={uploadAreaStyle}>
        <div css={iconStyle}>{EmptyProfileIcon}</div>
        <p css={text1}>
          {t('storygame_ai_onboarding_screen_description_upload_tip_frame')}
        </p>
        <p css={text2}>
          {t(
            'storygame_ai_onboarding_screen_description_upload_tip_image_type',
          )}
        </p>
        <div css={buttonContainer}>
          <button css={uploadButtonStyle} onClick={handleUploadClick}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M8.5 3V13M13.5 8L3.5 8"
                stroke="white"
                strokeOpacity="0.9"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            {t('storygame_ai_onboarding_screen_button_upload')}
          </button>
        </div>
      </div>
      <div css={noteStyle}>
        <p css={text3}>{t('storygame_ai_onboarding_screen_title_know_this')}</p>
        <p css={text4}>
          {t('storygame_ai_onboarding_screen_description_know_this_warning')}
        </p>
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/jpeg, image/png, image/webp"
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default StoryGameAiImageUpload;
